import React, { useRef, useEffect } from 'react';
import { Row, Col, Image } from "react-bootstrap";
import Container from '../components/Container';
import SectionHeader from '../components/SectionHeader';
import Button from '../components/Button';
import {
  Building2,
  Users,
  FileText,
  Award,
  Recycle,
  Leaf,
  ArrowRight,
  Briefcase
} from 'lucide-react';
import { motion, useInView, useAnimation } from 'framer-motion';
import '../styles/components.css';

const IMG_BANNER = "/images/bgprofessionels.jpg";
const IMG_INTRO = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop&q=80";
const IMG_SURMESURE = "https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=340&fit=crop&q=80";

const Professionnel = () => {
  const services = [
    {
      icon: Recycle,
      title: "Gestion et valorisation des déchets",
      text: "Enlèvement, gestion, caractérisation et revalorisation des déchets (carton, papiers, verre, textiles, métaux, matériel informatique, bio-médical).",
      color: "var(--color-primary)",
      image: "https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=400&h=260&fit=crop&q=80"
    },
    {
      icon: Users,
      title: "Formation et sensibilisation",
      text: "Formation de vos employés sur les pratiques éco-responsables et l'adoption d'outils d'éco-conception pour vos services et biens.",
      color: "var(--color-accent)",
      image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=400&h=260&fit=crop&q=80"
    },
    {
      icon: FileText,
      title: "Pack communication",
      text: "Communication et responsabilité autour de la revalorisation de vos biens et déchets pour mettre en avant vos actions durables.",
      color: "var(--color-secondary)",
      image: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=260&fit=crop&q=80"
    },
    {
      icon: Building2,
      title: "Solutions BTP",
      text: "Pack de communication pour accentuer les bonnes pratiques de durabilité sur vos sites et limiter l'impact environnemental de vos activités.",
      color: "var(--color-primary)",
      image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=260&fit=crop&q=80"
    },
    {
      icon: Leaf,
      title: "Solutions collectivités",
      text: "Plan de sensibilisation aux enjeux écologiques et mise en place d'actions en faveur du développement durable.",
      color: "var(--color-secondary)",
      image: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=400&h=260&fit=crop&q=80"
    },
    {
      icon: Award,
      title: "Accompagnement stratégique",
      text: "Élaboration et mise en œuvre de votre stratégie de maîtrise et de réduction de vos impacts environnementaux.",
      color: "var(--color-accent)",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=260&fit=crop&q=80"
    }
  ];

  const introRef = useRef(null);
  const servicesRef = useRef(null);
  const calloutRef = useRef(null);
  const ctaRef = useRef(null);

  const introInView = useInView(introRef, { once: true, margin: "-80px" });
  const servicesInView = useInView(servicesRef, { once: true, margin: "-80px" });
  const calloutInView = useInView(calloutRef, { once: true, margin: "-80px" });
  const ctaInView = useInView(ctaRef, { once: true, margin: "-80px" });

  const introControls = useAnimation();
  const servicesControls = useAnimation();
  const calloutControls = useAnimation();
  const ctaControls = useAnimation();

  useEffect(() => {
    if (introInView) introControls.start("visible");
  }, [introInView, introControls]);

  useEffect(() => {
    if (servicesInView) servicesControls.start("visible");
  }, [servicesInView, servicesControls]);

  useEffect(() => {
    if (calloutInView) calloutControls.start("visible");
  }, [calloutInView, calloutControls]);

  useEffect(() => {
    if (ctaInView) ctaControls.start("visible");
  }, [ctaInView, ctaControls]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12 }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const springTransition = { type: "spring", stiffness: 120, damping: 20 };

  return (
    <div style={{ overflow: 'hidden' }}>
      {/* Hero : image en arrière-plan, texte par-dessus */}
      <div
        role="img"
        aria-label="Services professionnels - entreprise et collectivités"
        style={{
          position: 'relative',
          width: '100%',
          height: '50vh',
          minHeight: 280,
          backgroundImage: `url(${IMG_BANNER})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.5) 100%)',
            zIndex: 0
          }}
        />
        <Container style={{ position: 'relative', zIndex: 1, width: '100%' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto', color: '#fff' }}
          >
            <motion.span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                opacity: 0.95,
                marginBottom: 'var(--spacing-md)'
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.95 }}
              transition={{ delay: 0.2 }}
            >
              <Briefcase size={18} />
              Nos services
            </motion.span>
            <h1
              style={{
                fontSize: 'clamp(2rem, 4.5vw, 3rem)',
                fontWeight: 'var(--font-weight-bold)',
                marginBottom: 'var(--spacing-lg)',
                lineHeight: 'var(--line-height-tight)',
                color: '#fff'
              }}
            >
              Services pour Professionnels
            </h1>
            <p
              style={{
                fontSize: 'var(--font-size-lg)',
                opacity: 0.95,
                lineHeight: 'var(--line-height-relaxed)',
                margin: 0,
                color: 'rgba(255, 255, 255, 0.95)'
              }}
            >
              Des solutions pour entreprises, BTP et collectivités. Gestion des déchets, formation et accompagnement stratégique.
            </p>
          </motion.div>
        </Container>
      </div>

      {/* Nos prestations */}
      <section className="section" style={{ backgroundColor: '#fff' }}>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
          >
            <SectionHeader
              title="Nos prestations"
              subtitle="Des solutions pour entreprises, BTP et collectivités"
            />
          </motion.div>
          <motion.div
            ref={servicesRef}
            initial="hidden"
            animate={servicesControls}
            variants={staggerContainer}
            style={{ maxWidth: 800, margin: 'var(--spacing-3xl) auto 0' }}
          >
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  style={{
                    paddingBottom: index < services.length - 1 ? 'var(--spacing-2xl)' : 0,
                    marginBottom: index < services.length - 1 ? 'var(--spacing-2xl)' : 0,
                    borderBottom: index < services.length - 1 ? '1px solid var(--color-gray-light)' : 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-lg)', flexWrap: 'wrap' }}>
                    {service.image && (
                      <div style={{ flexShrink: 0, borderRadius: 'var(--border-radius-lg)', overflow: 'hidden', width: 120, height: 90 }}>
                        <img src={service.image} alt={service.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    )}
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        background: service.color + '18',
                        color: service.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <Icon size={22} />
                    </div>
                    <div style={{ flex: '1', minWidth: 200 }}>
                      <h3
                        style={{
                          fontSize: 'var(--font-size-lg)',
                          fontWeight: 'var(--font-weight-semibold)',
                          color: 'var(--color-text)',
                          marginBottom: 'var(--spacing-sm)'
                        }}
                      >
                        {service.title}
                      </h3>
                      <p
                        style={{
                          fontSize: 'var(--font-size-base)',
                          color: 'var(--color-text-light)',
                          lineHeight: 'var(--line-height-relaxed)',
                          margin: 0
                        }}
                      >
                        {service.text}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </Container>
      </section>

      {/* Un panel de services adapté à vos enjeux */}
      <section className="section" style={{ backgroundColor: 'var(--color-bg)' }}>
        <Container>
          <motion.div
            ref={introRef}
            initial="hidden"
            animate={introControls}
            variants={fadeInUp}
          >
            <Row className="g-4 align-items-center">
              <Col md={5}>
                <div style={{ borderRadius: 'var(--border-radius-xl)', overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
                  <Image src={IMG_INTRO} alt="Services entreprises" fluid style={{ width: '100%', objectFit: 'cover' }} />
                </div>
              </Col>
              <Col md={7}>
            <h3
              style={{
                fontSize: 'var(--font-size-xl)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-text)',
                marginBottom: 'var(--spacing-lg)'
              }}
            >
              Un panel de services adapté à vos enjeux
            </h3>
            <p
              style={{
                fontSize: 'var(--font-size-lg)',
                lineHeight: 'var(--line-height-relaxed)',
                color: 'var(--color-text-light)',
                marginBottom: 'var(--spacing-xl)'
              }}
            >
              Nous vous proposons l'enlèvement, la gestion, la caractérisation et la revalorisation des déchets
              (carton, papiers, verre, textiles, métaux, matériel informatique, bio-médical). Pack communication
              et responsabilité autour de la revalorisation de vos biens et déchets. Formation de vos employés
              sur les pratiques éco-responsables et sur l'adoption d'outils d'éco-conception pour vos services et biens.
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-base)',
                lineHeight: 'var(--line-height-relaxed)',
                color: 'var(--color-text-light)',
                margin: 0
              }}
            >
              Acteurs des BTP : pack de communication pour les bonnes pratiques de durabilité sur vos sites et la réduction
              de votre impact carbone. Pour les collectivités : plan de sensibilisation aux enjeux écologiques et mise en place d'actions en faveur du développement durable.
            </p>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Solution sur-mesure : texte libre + image */}
      <section className="section" style={{ backgroundColor: 'var(--color-bg)' }}>
        <Container>
          <motion.div
            ref={calloutRef}
            initial="hidden"
            animate={calloutControls}
            variants={fadeInUp}
          >
            <Row className="g-4 align-items-center">
              <Col md={7}>
            <h4
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-text)',
                marginBottom: 'var(--spacing-md)'
              }}
            >
              Solution sur-mesure
            </h4>
            <p
              style={{
                fontSize: 'var(--font-size-base)',
                lineHeight: 'var(--line-height-relaxed)',
                color: 'var(--color-text-light)',
                margin: 0
              }}
            >
              Chaque entreprise est unique. Nous travaillons avec un panel de personnel formé, qualifié et expérimenté
              en gestion et traitement des déchets. Nous sommes à votre écoute pour concevoir une solution innovante
              sur-mesure adaptée à votre besoin.
            </p>
              </Col>
              <Col md={5}>
                <div style={{ borderRadius: 'var(--border-radius-xl)', overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
                  <Image src={IMG_SURMESURE} alt="Solution sur-mesure" fluid style={{ width: '100%', objectFit: 'cover' }} />
                </div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA */}
      <section
        className="section"
        style={{
          background: 'linear-gradient(90deg, var(--color-primary) 0%, var(--color-accent) 100%)',
          padding: 'var(--spacing-4xl) 0',
          textAlign: 'center'
        }}
      >
        <Container>
          <motion.div
            ref={ctaRef}
            initial="hidden"
            animate={ctaControls}
            variants={fadeInUp}
          >
            <h3
              style={{
                fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                fontWeight: 'var(--font-weight-bold)',
                color: '#fff',
                marginBottom: 'var(--spacing-md)'
              }}
            >
              Prêt à réduire votre impact environnemental ?
            </h3>
            <p
              style={{
                fontSize: 'var(--font-size-lg)',
                color: 'rgba(255,255,255,0.9)',
                marginBottom: 'var(--spacing-xl)'
              }}
            >
              Demandez un devis personnalisé. Nous vous répondons sous 48h.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              transition={springTransition}
            >
              <Button
                variant="light"
                size="lg"
                onClick={() => (window.location.href = '/contact')}
              >
                Demander un devis
                <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </section>
    </div>
  );
};

export default Professionnel;
